import { CloseOutlined } from '@ant-design/icons'
import { Select, Space } from 'antd'
import { CSSProperties } from 'react'
import {
  addTool,
  availableToolOptions,
  McpTool,
  removeTool,
} from '../McpTools/mcpToolsAdminModel'

/**
 * The rounded orange pill first used for the selected golden prompt. Exported
 * so both that label and the selected-tool labels share one definition. Callers
 * may spread extra properties (e.g. wrapping rules for long prompt text).
 */
export const SELECTED_LABEL_STYLE: CSSProperties = {
  display: 'inline-block',
  background: '#fa8c16',
  color: '#fff',
  borderRadius: 16,
  padding: '4px 14px',
}

type Props = {
  /** The MCP tool catalog to search. */
  tools: McpTool[]
  /** Currently selected tool names. */
  value: string[]
  onChange: (next: string[]) => void
  placeholder: string
}

/**
 * A search over MCP tools: picking one adds its name as a removable pill below
 * the search, styled like the selected golden prompt. Already-selected tools
 * drop out of the dropdown. Existing names that aren't in the catalog still
 * render as pills, so editing a case never silently drops a value.
 */
const McpToolMultiSelect = ({ tools, value, onChange, placeholder }: Props) => {
  const options = availableToolOptions(tools, value)
  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <Select
        showSearch
        // Controlled to null so the search always reads "add another", never a
        // single chosen value; the chosen names live in the pills below.
        value={null}
        onChange={(name: string | null) => name && onChange(addTool(value, name))}
        options={options}
        placeholder={placeholder}
        notFoundContent="No matching MCP tools."
        style={{ width: '100%' }}
      />
      {value.length > 0 && (
        <Space size={[8, 8]} wrap>
          {value.map((name) => (
            <span
              key={name}
              style={{
                ...SELECTED_LABEL_STYLE,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                fontFamily: 'monospace',
              }}
            >
              {name}
              <CloseOutlined
                onClick={() => onChange(removeTool(value, name))}
                style={{ cursor: 'pointer', fontSize: 11 }}
                aria-label={`Remove ${name}`}
              />
            </span>
          ))}
        </Space>
      )}
    </Space>
  )
}

export default McpToolMultiSelect
